import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const en = {
  nav: { stays: "Stays", admin: "Host", contact: "Contact" },
  cta: { browse: "View stays", view: "View", book: "Book", reserve: "Reserve", continue: "Continue", pay: "Pay" },
  home: {
    tagline: "Welcome home.",
    sub: "Book direct. No fees.",
    featured: "Stays",
    trust_title: "Direct booking",
    trust1: "Hosted personally.",
    trust2: "Signed agreement, secured by Stripe.",
    trust3: "No platform fees.",
    reviews: "Reviews",
  },
  listings: {
    title: "Stays",
    sub: "Pick your dates.",
    filter_dates: "Dates", filter_guests: "Guests", per_night: "/ night", avg_per_night: "avg / night",
    guests_one: "{{count}} guest", guests_other: "{{count}} guests",
    bedrooms: "bed", bathrooms: "bath", max_guests: "{{n}} guests",
    none: "Nothing available on those dates.",
  },
  detail: {
    amenities: "Amenities", house_rules: "House rules", reviews: "Reviews",
    description: "About", location: "Where",
    nightly: "Nightly", cleaning: "Cleaning", deposit: "Security deposit (refundable)",
    subtotal: "Subtotal", total: "Stay total", total_to_authorize: "Total",
    deposit_note: "Refundable hold — released after check-out.",
    min_stay: "Minimum {{n}} nights for these dates.",
    min_stay_hint: "Minimum stay: {{n}} nights",
    promo_label: "Promo code", promo_apply: "Apply", promo_remove: "Remove",
    promo_discount: "Discount ({{code}})", promo_invalid: "This code is not valid.", promo_applied: "Code applied.",
    check_in: "Check-in", check_out: "Check-out", guests: "Guests",
    select_dates: "Select dates", quote: "See price",
  },
  checkout: {
    title: "Almost done",
    sub: "Your details, the agreement, payment.",
    first_name: "First name", last_name: "Last name", email: "Email", phone: "Phone",
    country: "Country", notes: "Notes (optional)",
    agreement_title: "Rental agreement",
    agreement_intro: "Read and sign.",
    sign_here: "Sign here",
    clear: "Clear", signed: "Signed",
    must_sign: "Please sign before continuing.",
    breakdown: "Price",
  },
  confirmation: {
    title: "Booked.",
    sub: "Confirmation and signed agreement sent to your email.",
    back_home: "Home",
  },
  admin: {
    login_title: "Host login",
    login_sub: "Manage stays, bookings, calendar.",
    email: "Email", password: "Password", login: "Sign in",
    google: "Continue with Google",
    dashboard: "Dashboard", bookings: "Bookings", listings: "Stays",
    logout: "Sign out", status: "Status", revenue: "Revenue",
    guest: "Guest", dates: "Dates", paid: "Paid", total: "Total",
    add_listing: "Add stay", edit: "Edit", delete: "Delete",
    name: "Name", neighborhood: "Neighborhood", price: "Price/night",
    save: "Save", cancel: "Cancel",
    settings: "Settings",
    ical_export: "iCal export URL",
    ical_import: "Import iCals (Airbnb, Booking.com)",
    ical_paste: "Paste iCal URLs, one per line",
    price_overrides: "Per-date price overrides",
    price_overrides_hint: "Date (YYYY-MM-DD) and override price. Useful for weekends or holidays.",
    add_override: "Add override",
    deposit: "Deposit",
    capture_deposit: "Capture deposit",
    release_deposit: "Release deposit",
    run_review_emails: "Send post-stay review emails",
    review_emails_sent: "{{n}} review request(s) sent",
    promo_codes: "Promo codes", promo_new: "New code", promo_code: "Code", promo_type: "Type", promo_value: "Value",
    promo_percent: "Percent (%)", promo_fixed: "Fixed amount", promo_active: "Active", promo_uses: "Uses",
    promo_none: "No promo codes yet.", promo_hint: "Discounts apply to the nights only (cleaning fee and deposit excluded).",
    sync_prices_now: "Refresh prices now", last_sync: "Last Pricelabs sync", never_synced: "never", syncing: "Syncing…",
    sync_done: "Prices refreshed ({{n}} stay(s))", sync_auto: "auto-refresh every hour",
    resend_domain_title: "Verify your sending domain on Resend",
    resend_domain_body: "Guests currently see emails from onboarding@resend.dev. To send from a custom address (e.g. hello@staytlv.com), verify your domain at resend.com/domains, then update SENDER_EMAIL in backend/.env.",
    images_csv: "Image URLs (comma-separated)",
    rules_csv: "House rules (one per line)",
    description: "Description",
  },
  whatsapp: "Chat with the host",
  whatsapp_message: "Hi! I'd love to know more about your stays.",
  footer: { tagline: "Hosted in Tel Aviv.", rights: "All rights reserved." },
};

const he = {
  nav: { stays: "הדירות", admin: "מארח", contact: "צור קשר" },
  cta: { browse: "לדירות", view: "צפייה", book: "להזמין", reserve: "הזמנה", continue: "המשך", pay: "תשלום" },
  home: {
    tagline: "ברוך הבא הביתה.",
    sub: "הזמנה ישירה. ללא עמלות.",
    featured: "הדירות",
    trust_title: "הזמנה ישירה",
    trust1: "ארוח אישי.",
    trust2: "חוזה חתום, תשלום מאובטח ב-Stripe.",
    trust3: "ללא עמלות פלטפורמה.",
    reviews: "ביקורות",
  },
  listings: {
    title: "הדירות",
    sub: "בחרו תאריכים.",
    filter_dates: "תאריכים", filter_guests: "אורחים", per_night: "/ לילה", avg_per_night: "ממוצע / לילה",
    guests_one: "אורח אחד", guests_other: "{{count}} אורחים",
    bedrooms: "חדרים", bathrooms: "אמבטיה", max_guests: "עד {{n}} אורחים",
    none: "אין דירות פנויות לתאריכים אלה.",
  },
  detail: {
    amenities: "שירותים", house_rules: "כללי הבית", reviews: "ביקורות",
    description: "על הדירה", location: "מיקום",
    nightly: "מחיר ללילה", cleaning: "ניקיון", deposit: "פיקדון (חוזר)",
    subtotal: "ביניים", total: "סה״כ", total_to_authorize: "סה״כ לאישור",
    deposit_note: "פיקדון מוחזר — משוחרר לאחר היציאה.",
    min_stay: "מינימום {{n}} לילות לתאריכים אלה.",
    min_stay_hint: "שהות מינימלית: {{n}} לילות",
    promo_label: "קוד הנחה", promo_apply: "החל", promo_remove: "הסר",
    promo_discount: "הנחה ({{code}})", promo_invalid: "הקוד אינו תקף.", promo_applied: "הקוד הוחל.",
    check_in: "כניסה", check_out: "יציאה", guests: "אורחים",
    select_dates: "בחרו תאריכים", quote: "מחיר",
  },
  checkout: {
    title: "כמעט סיימנו",
    sub: "פרטים, חוזה, תשלום.",
    first_name: "שם פרטי", last_name: "שם משפחה", email: "אימייל", phone: "טלפון",
    country: "ארץ", notes: "הערות (לא חובה)",
    agreement_title: "חוזה שכירות",
    agreement_intro: "לקריאה וחתימה.",
    sign_here: "חתימה",
    clear: "נקה", signed: "נחתם",
    must_sign: "נא לחתום לפני המשך.",
    breakdown: "מחיר",
  },
  confirmation: {
    title: "מאושר.",
    sub: "אישור וחוזה חתום נשלחו לאימייל.",
    back_home: "בית",
  },
  admin: {
    login_title: "כניסת מארח",
    login_sub: "ניהול דירות, הזמנות ולוח זמנים.",
    email: "אימייל", password: "סיסמה", login: "כניסה",
    google: "המשך עם Google",
    dashboard: "לוח", bookings: "הזמנות", listings: "דירות",
    logout: "יציאה", status: "סטטוס", revenue: "הכנסות",
    guest: "אורח", dates: "תאריכים", paid: "שולם", total: "סה״כ",
    add_listing: "הוסף דירה", edit: "ערוך", delete: "מחק",
    name: "שם", neighborhood: "שכונה", price: "מחיר ללילה",
    save: "שמור", cancel: "ביטול",
    settings: "הגדרות",
    ical_export: "כתובת ייצוא iCal",
    ical_import: "ייבוא iCal (Airbnb, Booking.com)",
    ical_paste: "הדבק כתובות iCal, אחת בשורה",
    price_overrides: "תעריפים לפי תאריך",
    price_overrides_hint: "תאריך (YYYY-MM-DD) ותעריף. שימושי לסופי שבוע וחגים.",
    add_override: "הוסף תעריף",
    deposit: "פיקדון",
    capture_deposit: "חיוב פיקדון",
    release_deposit: "שחרור פיקדון",
    run_review_emails: "שלח אימיילי ביקורת אחרי השהייה",
    review_emails_sent: "{{n}} בקשות נשלחו",
    promo_codes: "קודי הנחה", promo_new: "קוד חדש", promo_code: "קוד", promo_type: "סוג", promo_value: "ערך",
    promo_percent: "אחוז (%)", promo_fixed: "סכום קבוע", promo_active: "פעיל", promo_uses: "שימושים",
    promo_none: "אין קודי הנחה עדיין.", promo_hint: "ההנחה חלה על הלילות בלבד (ללא ניקיון ופיקדון).",
    sync_prices_now: "רענן מחירים עכשיו", last_sync: "סנכרון Pricelabs אחרון", never_synced: "מעולם לא", syncing: "מסנכרן…",
    sync_done: "המחירים עודכנו ({{n}} דירות)", sync_auto: "רענון אוטומטי כל שעה",
    resend_domain_title: "אמת את הדומיין שלך ב-Resend",
    resend_domain_body: "האורחים רואים כעת אימיילים מ-onboarding@resend.dev. כדי לשלוח מכתובת מותאמת אישית (למשל hello@staytlv.com), אמת את הדומיין שלך ב-resend.com/domains ועדכן את SENDER_EMAIL ב-backend/.env.",
    images_csv: "כתובות תמונה (מופרדות בפסיק)",
    rules_csv: "כללי הבית (אחד בשורה)",
    description: "תיאור",
  },
  whatsapp: "צ׳אט עם המארח",
  whatsapp_message: "שלום! אשמח לדעת יותר על הדירות שלכם.",
  footer: { tagline: "באירוח בתל אביב.", rights: "כל הזכויות שמורות." },
};

const fr = {
  nav: { stays: "Séjours", admin: "Hôte", contact: "Contact" },
  cta: { browse: "Voir les séjours", view: "Voir", book: "Réserver", reserve: "Réserver", continue: "Continuer", pay: "Payer" },
  home: {
    tagline: "Bienvenue à la maison.",
    sub: "Réservation directe. Sans commission.",
    featured: "Séjours",
    trust_title: "Réservation directe",
    trust1: "Accueil personnel.",
    trust2: "Contrat signé, paiement sécurisé Stripe.",
    trust3: "Pas de frais de plateforme.",
    reviews: "Avis",
  },
  listings: {
    title: "Séjours",
    sub: "Choisissez vos dates.",
    filter_dates: "Dates", filter_guests: "Voyageurs", per_night: "/ nuit", avg_per_night: "moy. / nuit",
    guests_one: "{{count}} voyageur", guests_other: "{{count}} voyageurs",
    bedrooms: "ch.", bathrooms: "sdb", max_guests: "{{n}} voyageurs",
    none: "Aucun séjour disponible pour ces dates.",
  },
  detail: {
    amenities: "Équipements", house_rules: "Règles", reviews: "Avis",
    description: "À propos", location: "Localisation",
    nightly: "Tarif/nuit", cleaning: "Ménage", deposit: "Caution (remboursable)",
    subtotal: "Sous-total", total: "Total séjour", total_to_authorize: "Total à régler",
    deposit_note: "Caution remboursable — libérée après le départ.",
    min_stay: "Minimum {{n}} nuits pour ces dates.",
    min_stay_hint: "Séjour minimum : {{n}} nuits",
    promo_label: "Code promo", promo_apply: "Appliquer", promo_remove: "Retirer",
    promo_discount: "Remise ({{code}})", promo_invalid: "Ce code n'est pas valide.", promo_applied: "Code appliqué.",
    check_in: "Arrivée", check_out: "Départ", guests: "Voyageurs",
    select_dates: "Choisir les dates", quote: "Voir le prix",
  },
  checkout: {
    title: "Dernière étape",
    sub: "Vos infos, le contrat, le paiement.",
    first_name: "Prénom", last_name: "Nom", email: "Email", phone: "Téléphone",
    country: "Pays", notes: "Notes (facultatif)",
    agreement_title: "Contrat de location",
    agreement_intro: "À lire et signer.",
    sign_here: "Signature",
    clear: "Effacer", signed: "Signé",
    must_sign: "Merci de signer avant de continuer.",
    breakdown: "Prix",
  },
  confirmation: {
    title: "Réservé.",
    sub: "Confirmation et contrat signé envoyés par email.",
    back_home: "Accueil",
  },
  admin: {
    login_title: "Connexion hôte",
    login_sub: "Gérez vos séjours, réservations et calendrier.",
    email: "Email", password: "Mot de passe", login: "Connexion",
    google: "Continuer avec Google",
    dashboard: "Tableau de bord", bookings: "Réservations", listings: "Séjours",
    logout: "Déconnexion", status: "Statut", revenue: "Revenus",
    guest: "Voyageur", dates: "Dates", paid: "Payé", total: "Total",
    add_listing: "Ajouter un séjour", edit: "Modifier", delete: "Supprimer",
    name: "Nom", neighborhood: "Quartier", price: "Prix/nuit",
    save: "Enregistrer", cancel: "Annuler",
    settings: "Paramètres",
    ical_export: "URL d'export iCal",
    ical_import: "Importer des iCals (Airbnb, Booking.com)",
    ical_paste: "Collez les URLs iCal, une par ligne",
    price_overrides: "Tarifs par date",
    price_overrides_hint: "Date (YYYY-MM-DD) et tarif. Pratique pour weekends ou fêtes.",
    add_override: "Ajouter un tarif",
    deposit: "Caution",
    capture_deposit: "Encaisser la caution",
    release_deposit: "Libérer la caution",
    run_review_emails: "Envoyer les emails d'avis post-séjour",
    review_emails_sent: "{{n}} demande(s) envoyée(s)",
    promo_codes: "Codes promo", promo_new: "Nouveau code", promo_code: "Code", promo_type: "Type", promo_value: "Valeur",
    promo_percent: "Pourcentage (%)", promo_fixed: "Montant fixe", promo_active: "Actif", promo_uses: "Utilisations",
    promo_none: "Aucun code promo pour l'instant.", promo_hint: "La remise s'applique aux nuits uniquement (ménage et caution exclus).",
    sync_prices_now: "Rafraîchir les prix maintenant", last_sync: "Dernière synchro Pricelabs", never_synced: "jamais", syncing: "Synchronisation…",
    sync_done: "Prix rafraîchis ({{n}} logement(s))", sync_auto: "rafraîchissement auto toutes les heures",
    resend_domain_title: "Vérifiez votre domaine d'envoi sur Resend",
    resend_domain_body: "Les voyageurs reçoivent actuellement les emails depuis onboarding@resend.dev. Pour utiliser une adresse personnalisée (ex : hello@staytlv.com), vérifiez votre domaine sur resend.com/domains, puis mettez à jour SENDER_EMAIL dans backend/.env.",
    images_csv: "URLs d'images (séparées par des virgules)",
    rules_csv: "Règles (une par ligne)",
    description: "Description",
  },
  whatsapp: "Discuter avec l'hôte",
  whatsapp_message: "Bonjour ! J'aimerais en savoir plus sur vos séjours.",
  footer: { tagline: "Hébergé à Tel Aviv.", rights: "Tous droits réservés." },
};

i18n
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en }, he: { translation: he }, fr: { translation: fr } },
    lng: localStorage.getItem("lang") || "en",
    fallbackLng: "en",
    interpolation: { escapeValue: false },
  });

export const setLanguage = (lng) => {
  localStorage.setItem("lang", lng);
  i18n.changeLanguage(lng);
  document.documentElement.lang = lng;
  document.documentElement.dir = lng === "he" ? "rtl" : "ltr";
};

document.documentElement.lang = i18n.language;
document.documentElement.dir = i18n.language === "he" ? "rtl" : "ltr";

export default i18n;
